import { useRef, useEffect } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, ArrowRight } from 'lucide-react';
import Hero from '../components/Hero';
import { serviceCategories } from './services/serviceCategories';
import { usePageMeta } from '../hooks/usePageMeta';
import { useStructuredData, organizationSchema } from '../hooks/useStructuredData';
import '../styles/HomeServices.css';

function useDragScroll(ref) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let isDown = false, startX = 0, scrollLeft = 0;

    const onDown  = (e) => { isDown = true; el.classList.add('is-dragging'); startX = e.pageX - el.offsetLeft; scrollLeft = el.scrollLeft; };
    const onUp    = ()  => { isDown = false; el.classList.remove('is-dragging'); };
    const onLeave = ()  => { isDown = false; el.classList.remove('is-dragging'); };
    const onMove  = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x    = e.pageX - el.offsetLeft;
      const walk = (x - startX) * 1.5;
      el.scrollLeft = scrollLeft - walk;
    };

    el.addEventListener('mousedown',  onDown);
    el.addEventListener('mouseup',    onUp);
    el.addEventListener('mouseleave', onLeave);
    el.addEventListener('mousemove',  onMove);
    return () => {
      el.removeEventListener('mousedown',  onDown);
      el.removeEventListener('mouseup',    onUp);
      el.removeEventListener('mouseleave', onLeave);
      el.removeEventListener('mousemove',  onMove);
    };
  }, [ref]);
}

export default function HomePage() {
  const MotionLink = motion.create(Link);
  const scrollRef  = useRef(null);
  const { openEnquiry } = useOutletContext();

  usePageMeta(
    'Kodefactor | IT Consulting & Digital Transformation',
    'Expert IT consulting, cloud migration, cybersecurity, DevOps, and AI engineering for businesses ready to scale.',
    'https://kodefactor.io'
  );

  useStructuredData(organizationSchema);

  useDragScroll(scrollRef);

  const scrollBy  = (dir) => scrollRef.current?.scrollBy({ left: dir * 400, behavior: 'smooth' });

  return (
    <div>
      <Hero />

      {/* ── Services ───────────────────────────────────── */}
      <section className="section home-whatwedo">
        <div className="container">
          <motion.div
            className="services__header"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6 }}
          >
            <div className="section__tag">What We Do</div>
            <h2 className="section__title">Services Built for <span>Modern Business</span></h2>
            <p className="section__sub">
              From cloud infrastructure to AI automation — scroll through our most requested services.
            </p>
          </motion.div>
        </div>

        <div className="hsvc-wrap">
          <button className="hsvc-nav hsvc-nav--left"  onClick={() => scrollBy(-1)} aria-label="Previous">‹</button>
          <button className="hsvc-nav hsvc-nav--right" onClick={() => scrollBy(1)}  aria-label="Next">›</button>

          <div className="hsvc-scroll" ref={scrollRef}>
            {serviceCategories.map((svc, idx) => (
              <motion.article
                key={svc.slug}
                className="hsvc-card"
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.55, delay: Math.min(idx * 0.07, 0.3), ease: [0.22, 1, 0.36, 1] }}
              >
                <div
                  className="hsvc-card__top"
                  style={{
                    backgroundImage: `linear-gradient(140deg, ${svc.accentColor}f0 0%, ${svc.accentColor}b0 100%), url(${svc.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <span className="hsvc-card__idx">{String(idx + 1).padStart(2, '0')}</span>
                  <div className="hsvc-card__overlay-content">
                    <h3 className="hsvc-card__overlay-title">{svc.title}</h3>
                    <span className="hsvc-card__top-tag">{svc.tag}</span>
                  </div>
                </div>

                <div className="hsvc-card__body">
                  <div>
                    <p className="hsvc-card__desc">{svc.subtitle}</p>
                  </div>

                  <div className="hsvc-card__pills">
                    {svc.features.map((f) => (
                      <span
                        key={f}
                        className="hsvc-card__pill"
                        style={{ color: svc.accentColor, background: svc.accentColor + '18', borderColor: svc.accentColor + '45' }}
                      >
                        {f}
                      </span>
                    ))}
                  </div>

                  <ul className="hsvc-card__bullets">
                    {svc.bullets.slice(0, 3).map((b, i) => (
                      <li key={i} className="hsvc-card__bullet">
                        <Check size={18} className="hsvc-card__check" style={{ color: svc.accentColor, background: svc.accentColor + '18' }} />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to={`/services/${svc.slug}`}
                    className="hsvc-card__cta"
                    style={{ color: svc.accentColor, borderColor: svc.accentColor + '45' }}
                  >
                    <span>View full details</span>
                    <ArrowRight size={16} className="hsvc-card__cta-arrow" />
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>
        </div>

        <div className="container">
          <div className="home-services__actions">
            <MotionLink
              to="/services"
              className="btn btn--outline"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
            >
              View all services
            </MotionLink>
            <motion.button
              onClick={() => openEnquiry('Website')}
              className="btn btn--primary"
              whileHover={{ scale: 1.05, boxShadow: '0 0 50px rgba(37,111,168,0.45)' }}
              whileTap={{ scale: 0.97 }}
            >
              Get a proposal <ArrowRight size={16} style={{ display: 'inline' }} />
            </motion.button>
          </div>
        </div>
      </section>

      {/* ── Delivery You Can Measure ───────────────────── */}
      <section className="section hproof">
        <div className="container">

          <motion.div
            className="hproof__hdr"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6 }}
          >
            <div className="section__tag">Track Record</div>
            <h2 className="section__title">Delivery You Can <span>Measure</span></h2>
            <p className="section__sub">
              Senior engineers, clear roadmaps, reliable execution — every engagement is backed by concrete outcomes.
            </p>
          </motion.div>

          <div className="hproof__grid">
            {[
              {
                image:  'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=700&h=260&fit=crop',
                badge:  'Security',
                accent: '#0891b2',
                title:  'Security-First by Default',
                desc:   'Threat modeling, vulnerability assessments, and hardened guardrails are built into every engagement from day one — not bolted on as an afterthought. We make security a delivery property, not a separate workstream.',
                points: [
                  'Penetration testing & red team exercises with full remediation reports',
                  'Compliance-ready frameworks: ISO 27001, SOC 2, GDPR, and HIPAA',
                ],
              },
              {
                image:  'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=700&h=260&fit=crop',
                badge:  'Infrastructure',
                accent: '#2563eb',
                title:  'Cloud & Platform Expertise',
                desc:   'Architecture that actually scales. We design for observability, SRE discipline, and cost efficiency across AWS, Azure, and GCP — so your platform grows with your business without runaway bills or surprise outages.',
                points: [
                  'Multi-cloud architecture, cost optimisation, and right-sizing',
                  'CI/CD pipelines, Kubernetes orchestration, and infrastructure-as-code',
                ],
              },
              {
                image:  'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=700&h=260&fit=crop',
                badge:  'Delivery',
                accent: '#7c3aed',
                title:  'Fast, Transparent Execution',
                desc:   'Weekly milestones, async written updates, and no surprise scope creep. You always know exactly where your project stands, what comes next, and why we made every technical decision along the way.',
                points: [
                  'Structured sprint cadence with milestone sign-off at every stage',
                  'Dedicated client success engineer assigned to every account',
                ],
              },
            ].map((card, i) => (
              <motion.div
                key={i}
                className="hproof-card"
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                style={{ '--card-accent': card.accent }}
              >
                <div
                  className="hproof-card__img"
                  style={{ backgroundImage: `url(${card.image})` }}
                >
                  <div className="hproof-card__img-fade" />
                  <span
                    className="hproof-card__badge"
                    style={{ color: card.accent, background: card.accent + '1c', borderColor: card.accent + '55' }}
                  >
                    {card.badge}
                  </span>
                </div>

                <div className="hproof-card__body">
                  <h3 className="hproof-card__title">{card.title}</h3>
                  <p  className="hproof-card__desc">{card.desc}</p>
                  <ul className="hproof-card__points">
                    {card.points.map((pt, j) => (
                      <li key={j} className="hproof-card__point">
                        <ArrowRight size={16} className="hproof-card__check" style={{ color: card.accent }} />
                        <span>{pt}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>

        </div>
      </section>

    </div>
  );
}
